import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getJobOpenings } from "../../../ApiServices/ApiServices";

const JobList = () => {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("A to Z");
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [experience, setExperience] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMoreDescription, setShowMoreDescription] = useState({});
  const [showAllLocations, setShowAllLocations] = useState({});
  const [showMoreSkills, setShowMoreSkills] = useState({});

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobs = await getJobOpenings();
        setJobs(jobs?.jobs || []);
        setLoading(false);
      } catch (error) {
        setError(error?.response?.data?.message || "Error fetching jobs");
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  // Toggle a specific job's visibility for descriptions, locations, and skills
  const toggleState = (stateSetter, jobId) => {
    stateSetter((prevState) => ({
      ...prevState,
      [jobId]: !prevState[jobId],
    }));
  };

  // Filter jobs based on search, location, department, and experience
  const filteredJobs = jobs
    .filter((job) => job?.title?.toLowerCase().includes(search))
    .filter((job) => (!location ? true : job?.locations.includes(location)))
    .filter((job) => (!department ? true : job?.department === department))
    .filter((job) => {
      if (!experience) return true;
      if (experience === "Intern") return job.experience === "0";
      if (experience === "Entry Level") return job.experience <= 2;
      if (experience === "Experienced") return job.experience > 2;
      return true;
    })
    .sort((a, b) =>
      sort === "A to Z"
        ? a?.title?.localeCompare(b.title)
        : b?.title?.localeCompare(a.title)
    );

  return (
    <div className="p-6 font-sans bg-gray-100 min-h-screen mt-14">
      <div className="max-w-6xl mx-auto">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <input
            type="text"
            placeholder="Search for Jobs"
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
            className="flex-grow px-4 py-2 border border-gray-300 rounded-md"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md"
          >
            <option value="A to Z">Sort By: A to Z</option>
            <option value="Z to A">Sort By: Z to A</option>
          </select>
          <select
            onChange={(e) => setLocation(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Locations</option>
            <option value="Mumbai">Mumbai</option>
            <option value="Bangalore">Bangalore</option>
            <option value="Delhi">Delhi</option>
          </select>
          <select
            onChange={(e) => setDepartment(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Departments</option>
            <option value="IT">IT</option>
            <option value="Sales">Sales</option>
            <option value="HR">HR</option>
            <option value="Finance">Finance</option>
          </select>
          <select
            onChange={(e) => setExperience(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Experience</option>
            <option value="Intern">Intern</option>
            <option value="Entry Level">Entry Level</option>
            <option value="Experienced">Experienced</option>
          </select>
        </div>

        {/* Loading and Error Handling */}
        {loading && (
          <p className="text-center text-gray-600">Loading jobs...</p>
        )}
        {error && <p className="text-center text-red-500">Error: {error}</p>}
        {!loading && !error && filteredJobs.length === 0 && (
          <p className="text-center text-gray-600">No jobs found.</p>
        )}

        {/* Job Listings */}
        <div className="grid grid-cols-1 gap-6">
          {filteredJobs.map((job) => (
            <Link
              to={`/jobs/details?jobId=${job?.jobId}&role=${encodeURIComponent(
                job?.title
              )}`}
              key={job?.id}
              className="p-6 border border-gray-300 rounded-lg bg-gray-50 hover:bg-blue-100 hover:border-blue-300"
            >
              <div className="block text-xl font-semibold text-blue-500">
                {job.title}
              </div>
              <p className="text-sm text-gray-500">Job ID: {job?.jobId}</p>

              {/* Location Toggle */}
              <p className="text-gray-700 mt-2">
                Location:{" "}
                {showAllLocations[job.id]
                  ? job.locations.join(", ")
                  : job.locations[0]}
                {job?.locations?.length > 1 && (
                  <span
                    className="text-blue-500 cursor-pointer ml-2"
                    // onClick={() => toggleState(setShowAllLocations, job.id)}
                  >
                    {showAllLocations[job.id] ? "…show less" : "…more"}
                  </span>
                )}
              </p>

              <p className="mt-2">
                {showMoreDescription[job.id]
                  ? job?.description
                  : job?.description.slice(0, 100)}
                {job?.description?.length > 100 && (
                  <span
                    className="text-blue-500 cursor-pointer"
                    onClick={() => toggleState(setShowMoreDescription, job.id)}
                  >
                    {showMoreDescription[job.id] ? "…show less" : "…show more"}
                  </span>
                )}
              </p>

              <div className="mt-4 flex gap-2 flex-wrap">
                {job?.skills
                  ?.slice(0, showMoreSkills[job.id] ? job?.skills?.length : 3)
                  .map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                {job?.skills?.length > 3 && (
                  <span
                    className="text-blue-500 cursor-pointer"
                    onClick={() => toggleState(setShowMoreSkills, job.id)}
                  >
                    {showMoreSkills[job.id]
                      ? "…show less"
                      : `+${job?.skills?.length - 3} more`}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default JobList;
